import { identifier, type Paginated } from '@deque/pagination-utils';
import { useCallback } from 'react';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import useAPI from '~/common/hooks/useAPI';
import billingClient from '~/common/utils/billing-client/client-v2';
import { type EnterpriseTeamWithMembers } from '~/common/utils/billing-client/teams/teams';

export const csvDefaultHeaders = [
  'team_id',
  'team_name',
  'user_keycloak_id',
  'email',
  'username',
  'user_added_at'
];

const customHeaderNamesMap: Record<string, string> = {
  team_id: 'id',
  team_name: 'name'
};

const fetchAllTeamsWithMembers = async (
  enterpriseId: string,
  token: string,
  perPage = 1000
): Promise<EnterpriseTeamWithMembers[]> => {
  let page = 1;
  const data: EnterpriseTeamWithMembers[] = [];
  let isDataLeft = true;

  while (isDataLeft) {
    const result: Paginated<EnterpriseTeamWithMembers[]> =
      await billingClient.teams.listWithMembers({
        enterpriseId,
        token,
        page,
        per_page: perPage
      });

    data.push(...result);
    page++;

    const { has_next } = result[identifier];
    isDataLeft = has_next;
  }

  return data;
};

const useExportEnterpriseTeamsToCSV = () => {
  const { activeEnterprise } = useEnterprises();
  const { user } = useAuthContext();

  const enterpriseId = activeEnterprise?.id;
  const token = user?.token;

  const dataLoader = useCallback(() => {
    if (!enterpriseId || !token) {
      return Promise.resolve(null);
    }

    return fetchAllTeamsWithMembers(enterpriseId, token);
  }, [enterpriseId, token]);

  const { loading, error, data, refresh } = useAPI(dataLoader);

  const processData = useCallback(
    (headers = csvDefaultHeaders) => {
      if (!data || !data.length) {
        return '';
      }

      // If headers aren't provided, use object keys from first data item
      const csvHeaders = headers.length > 0 ? headers : Object.keys(data[0]);

      // Create CSV header row
      const headerRow = csvHeaders.join(',');

      // Create data rows
      const csvRows = data.map(item => {
        return csvHeaders
          .map((header: string) => {
            const fieldKey =
              customHeaderNamesMap[header] ??
              (header as keyof EnterpriseTeamWithMembers);

            const field =
              item[fieldKey as keyof EnterpriseTeamWithMembers]?.toString() ??
              '';

            if (
              field.includes(',') ||
              field.includes('"') ||
              field.includes('\n')
            ) {
              return `"${field.replace(/"/g, '""')}"`;
            }

            return field;
          })
          .join(',');
      });

      // Combine headers and data
      return [headerRow, ...csvRows].join('\n');
    },
    [data]
  );

  return {
    processData,
    isEnterpriseTeamsDataLoading: loading,
    enterpriseTeamsDataError: error,
    refreshEnterpriseTeamsData: refresh
  };
};

export default useExportEnterpriseTeamsToCSV;
